import {
  MAP_INITIAL_NWLAT,
  MAP_INITIAL_NWLNG,
  MAP_INITIAL_SELAT,
  MAP_INITIAL_SELNG,
  MAP_INITIAL_ZOOM,
  MAX_AGE,
  MIN_AGE,
} from '@kamernet/constants/Search';
import {
  AvailabilityPeriodEnum,
  Furnishings,
  GenderEnum,
  OwnerTypeID,
  RadiusID,
  RentalPriceID,
  RoomAdvertSortOptionID,
  RoomTypeID,
  RoommateNumberID,
  SearchView,
  Statuses,
  SuitableForNumberID,
  SurfaceMinimumID,
} from '@kamernet/core/ApiClient/Kamernet';
import { Locale } from '@kamernet/core/Intl/types';
import {
  valueExistsInArray,
  filterArrayByAllowedValues,
} from '@kamernet/utilities/Array';
import {
  parseRouteParamToBoolean,
  parseRouteParamToEnum,
  parseRouteParamToEnumArray,
  parseRouteParamToFloat,
  parseRouteParamToInt,
} from '@kamernet/utilities/URL';

import { defineRoute } from '../defineRoute';
import { RequiredRouteParams } from '../types';

export enum ListingsSearchCategory {
  Empty = 0,
  Room = 1,
  Apartment = 2,
  Studio = 4,
  Antisquat = 8,
  StudentHousing = 16,
  FurnishedApartment = 17,
  ShortTermRental = 18,
  LongTermRental = 19,
  Generic = 20,
}

export const ListingsSearchAllowedSuitableForGenders = [
  GenderEnum.Male,
  GenderEnum.Female,
] as const;

export const ListingsSearchAllowedRoommateGenders = [
  GenderEnum.Male,
  GenderEnum.Female,
  GenderEnum.Mixed,
] as const;

export const ListingsSearchAllowedRoommateNumberMaxes = [
  RoommateNumberID.None,
  RoommateNumberID.Four,
  RoommateNumberID.Eight,
  RoommateNumberID.MoreThanEight,
] as const;

export const ListingsSearchAllowedSearchViews = [
  SearchView.List,
  SearchView.Map,
] as const;

export const AGE_RANGE = Array.from(
  { length: MAX_AGE - MIN_AGE + 1 },
  (_, i) => MIN_AGE + i,
);

export type ListingsSearchRouteParams = {
  city?: string | null;
  street?: string | null;
  searchCategories?: ListingsSearchCategory[] | null;
  pageNo?: number | null;
  radius?: RadiusID | null;
  minSize?: SurfaceMinimumID | null;
  maxRent?: RentalPriceID | null;
  suitableForStatusIds?: Statuses[] | null;
  searchView?: SearchView | null;
  psaIdentifier?: string | null;
  sort?: RoomAdvertSortOptionID | null;
  furnishings?: Furnishings[] | null;
  availabilityPeriods?: AvailabilityPeriodEnum[] | null;
  availableFrom?: string | null;
  hasInternet?: boolean | null;
  isBathroomPrivate?: boolean | null;
  isKitchenPrivate?: boolean | null;
  isToiletPrivate?: boolean | null;
  suitableForNumberOfPersons?: SuitableForNumberID | null;
  suitableForGenders?: GenderEnum[] | null;
  suitableForAge?: number | null;
  suitableForWorkStatuses?: Statuses[] | null;
  isSmokingInsideAllowed?: boolean | null;
  isPetsInsideAllowed?: boolean | null;
  roommateNumberMax?: RoommateNumberID | null;
  roommateGenders?: GenderEnum[] | null;
  ownerTypes?: OwnerTypeID[] | null;
  nwlat?: number | null;
  nwlng?: number | null;
  selat?: number | null;
  selng?: number | null;
  mapZoom?: number | null;
  mapMarkerLat?: number | null;
  mapMarkerLng?: number | null;
};

export type ListingsSearchRouteParamsString = {
  searchCategoryAndCity: string;
  street?: string;
  pageNo?: string;
  radius?: string;
  minSize?: string;
  maxRent?: string;
  searchCategories?: string;
  suitableForStatusIds?: string;
  searchView?: string;
  psaIdentifier?: string;
  sort?: string;
  furnishings?: string;
  availabilityPeriods?: string;
  availableFrom?: string;
  hasInternet?: string;
  isBathroomPrivate?: string;
  isKitchenPrivate?: string;
  isToiletPrivate?: string;
  suitableForNumberOfPersons?: string;
  suitableForGenders?: string;
  suitableForAge?: string;
  suitableForWorkStatuses?: string;
  isSmokingInsideAllowed?: string;
  isPetsInsideAllowed?: string;
  roommateNumberMax?: string;
  roommateGenders?: string;
  ownerTypes?: string;
  nwlat?: string;
  nwlng?: string;
  selat?: string;
  selng?: string;
  mapZoom?: string;
  mapMarkerLat?: string;
  mapMarkerLng?: string;
};

export function getMainListingsSearchCategory(
  searchCategories: ListingsSearchCategory[] | null | undefined,
): ListingsSearchCategory {
  if (!searchCategories) {
    return ListingsSearchCategory.Generic;
  }

  if (searchCategories.length === 1 && searchCategories[0] != null) {
    return searchCategories[0];
  }

  return ListingsSearchCategory.Generic;
}

const SEARCH_CATEGORY_TO_STRING_EN: Record<ListingsSearchCategory, string> = {
  [ListingsSearchCategory.Empty]: '',
  [ListingsSearchCategory.Generic]: 'properties',
  [ListingsSearchCategory.Room]: 'room',
  [ListingsSearchCategory.Apartment]: 'apartment',
  [ListingsSearchCategory.Studio]: 'studio',
  [ListingsSearchCategory.Antisquat]: 'anti-squat',
  [ListingsSearchCategory.StudentHousing]: 'student-housing',
  [ListingsSearchCategory.FurnishedApartment]: 'furnished-apartments',
  [ListingsSearchCategory.LongTermRental]: 'long-term-rentals',
  [ListingsSearchCategory.ShortTermRental]: 'short-term-rentals',
};

const SEARCH_CATEGORY_TO_STRING_NL: Record<ListingsSearchCategory, string> = {
  [ListingsSearchCategory.Empty]: '',
  [ListingsSearchCategory.Generic]: 'huurwoningen',
  [ListingsSearchCategory.Room]: 'kamer',
  [ListingsSearchCategory.Apartment]: 'appartement',
  [ListingsSearchCategory.Studio]: 'studio',
  [ListingsSearchCategory.Antisquat]: 'anti-kraak',
  [ListingsSearchCategory.StudentHousing]: 'studentenwoning',
  [ListingsSearchCategory.FurnishedApartment]: 'gemeubileerde-appartementen',
  [ListingsSearchCategory.LongTermRental]: 'lange-termijn-huren',
  [ListingsSearchCategory.ShortTermRental]: 'tijdelijk-huren',
};

const STRING_TO_SEARCH_CATEGORY_EN: Record<string, ListingsSearchCategory> = {
  properties: ListingsSearchCategory.Generic,
  room: ListingsSearchCategory.Room,
  apartment: ListingsSearchCategory.Apartment,
  studio: ListingsSearchCategory.Studio,
  'anti-squat': ListingsSearchCategory.Antisquat,
  'student-housing': ListingsSearchCategory.StudentHousing,
  'furnished-apartments': ListingsSearchCategory.FurnishedApartment,
  'long-term-rentals': ListingsSearchCategory.LongTermRental,
  'short-term-rentals': ListingsSearchCategory.ShortTermRental,
};

const STRING_TO_SEARCH_CATEGORY_NL: Record<string, ListingsSearchCategory> = {
  huurwoningen: ListingsSearchCategory.Generic,
  kamer: ListingsSearchCategory.Room,
  appartement: ListingsSearchCategory.Apartment,
  studio: ListingsSearchCategory.Studio,
  'anti-kraak': ListingsSearchCategory.Antisquat,
  studentenwoning: ListingsSearchCategory.StudentHousing,
  'gemeubileerde-appartementen': ListingsSearchCategory.FurnishedApartment,
  'lange-termijn-huren': ListingsSearchCategory.LongTermRental,
  'tijdelijk-huren': ListingsSearchCategory.ShortTermRental,
};

const COUNTRY_SLUG_BY_LOCALE: Record<Locale, string> = {
  [Locale.en]: 'netherlands',
  [Locale.nl]: 'nederland',
};

const SEARCH_CATEGORY_TO_LISTING_TYPE: Record<
  ListingsSearchCategory,
  RoomTypeID | null
> = {
  [ListingsSearchCategory.Empty]: null,
  [ListingsSearchCategory.Generic]: null,
  [ListingsSearchCategory.Room]: RoomTypeID.Room,
  [ListingsSearchCategory.Apartment]: RoomTypeID.Apartment,
  [ListingsSearchCategory.Studio]: RoomTypeID.Studio,
  [ListingsSearchCategory.Antisquat]: RoomTypeID.Antikraak,
  [ListingsSearchCategory.StudentHousing]: null,
  [ListingsSearchCategory.FurnishedApartment]: null,
  [ListingsSearchCategory.LongTermRental]: null,
  [ListingsSearchCategory.ShortTermRental]: null,
};

const LISTING_TYPE_TO_SEARCH_CATEGORY: Record<
  RoomTypeID,
  ListingsSearchCategory
> = {
  [RoomTypeID.Room]: ListingsSearchCategory.Room,
  [RoomTypeID.Apartment]: ListingsSearchCategory.Apartment,
  [RoomTypeID.Studio]: ListingsSearchCategory.Studio,
  [RoomTypeID.Antikraak]: ListingsSearchCategory.Antisquat,
  [RoomTypeID.StudentResidence]: ListingsSearchCategory.StudentHousing,
};

const keepAgeInRange = (age: number | null | undefined): number | null => {
  if (!age) {
    return null;
  }

  if (age < MIN_AGE) {
    return MIN_AGE;
  }

  if (age > MAX_AGE) {
    return MAX_AGE;
  }

  return age;
};

const routeParamsStringifier = (
  routeParams: ListingsSearchRouteParams,
  locale: Locale,
): ListingsSearchRouteParamsString => {
  const suitableForGenders = filterArrayByAllowedValues(
    routeParams.suitableForGenders || [],
    ListingsSearchAllowedSuitableForGenders,
  );

  const roommateNumberMax = valueExistsInArray(
    routeParams.roommateNumberMax,
    ListingsSearchAllowedRoommateNumberMaxes,
  )
    ? routeParams.roommateNumberMax
    : undefined;

  const searchView = valueExistsInArray(
    routeParams.searchView,
    ListingsSearchAllowedSearchViews,
  )
    ? routeParams.searchView
    : undefined;

  const roommateGenders = filterArrayByAllowedValues(
    routeParams.roommateGenders || [],
    ListingsSearchAllowedRoommateGenders,
  );

  const mainSearchCateogry = getMainListingsSearchCategory(
    routeParams.searchCategories,
  );

  if (mainSearchCateogry === ListingsSearchCategory.Empty) {
    throw new Error('main search category can not be empty');
  }

  const city = routeParams.city
    ? routeParams.city
    : COUNTRY_SLUG_BY_LOCALE[locale];

  return {
    searchCategoryAndCity: `${
      locale === Locale.en
        ? SEARCH_CATEGORY_TO_STRING_EN[mainSearchCateogry]
        : SEARCH_CATEGORY_TO_STRING_NL[mainSearchCateogry]
    }-${city}`,
    street: routeParams.street ?? undefined,
    pageNo: routeParams.pageNo?.toString(),
    radius: routeParams.radius?.toString(),
    minSize: routeParams.minSize?.toString(),
    maxRent: routeParams.maxRent?.toString(),
    searchCategories:
      routeParams.searchCategories?.length === 1
        ? undefined
        : routeParams.searchCategories?.join(','),
    suitableForStatusIds: routeParams.suitableForStatusIds?.join(','),
    searchView: searchView?.toString(),
    psaIdentifier: routeParams.psaIdentifier?.toString(),
    sort: routeParams.sort?.toString(),
    furnishings: routeParams.furnishings?.join(','),
    availabilityPeriods: routeParams.availabilityPeriods?.join(','),
    availableFrom: routeParams.availableFrom?.toString(),
    hasInternet: routeParams.hasInternet?.toString(),
    isBathroomPrivate: routeParams.isBathroomPrivate?.toString(),
    isKitchenPrivate: routeParams.isKitchenPrivate?.toString(),
    isToiletPrivate: routeParams.isToiletPrivate?.toString(),
    suitableForNumberOfPersons:
      routeParams.suitableForNumberOfPersons?.toString(),
    suitableForGenders: suitableForGenders.join(','),
    suitableForAge: keepAgeInRange(routeParams.suitableForAge)?.toString(),
    suitableForWorkStatuses: routeParams.suitableForWorkStatuses?.join(','),
    isSmokingInsideAllowed: routeParams.isSmokingInsideAllowed?.toString(),
    isPetsInsideAllowed: routeParams.isPetsInsideAllowed?.toString(),
    roommateNumberMax: roommateNumberMax?.toString(),
    roommateGenders: roommateGenders.join(','),
    ownerTypes: routeParams.ownerTypes?.join(','),
    nwlat: routeParams.nwlat?.toString(),
    nwlng: routeParams.nwlng?.toString(),
    selat: routeParams.selat?.toString(),
    selng: routeParams.selng?.toString(),
    mapZoom: routeParams.mapZoom?.toString(),
    mapMarkerLat: routeParams.mapMarkerLat?.toString(),
    mapMarkerLng: routeParams.mapMarkerLng?.toString(),
  };
};

const routeParamsParser = (
  routeParamsString: ListingsSearchRouteParamsString,
  locale: Locale,
): RequiredRouteParams<ListingsSearchRouteParams> => {
  let city: string | null = null;
  let mainSearchCategory: ListingsSearchCategory | null = null;
  let mainSearchCategoryString: string | null = null;

  for (const [key, value] of Object.entries(
    locale === Locale.en
      ? STRING_TO_SEARCH_CATEGORY_EN
      : STRING_TO_SEARCH_CATEGORY_NL,
  )) {
    if (routeParamsString.searchCategoryAndCity.startsWith(key)) {
      mainSearchCategoryString = key;
      mainSearchCategory = value;
    }
  }

  try {
    if (!mainSearchCategory || !mainSearchCategoryString) {
      throw new Error('search category is not valid');
    }

    city = routeParamsString.searchCategoryAndCity
      .replace(`${mainSearchCategoryString}-`, '')
      .trim()
      .toLowerCase();

    if (!city) {
      throw new Error('city can not be empty');
    }
  } catch {
    city = '';
    mainSearchCategory = ListingsSearchCategory.Empty;
  }

  const isCountrySearch = Object.values(COUNTRY_SLUG_BY_LOCALE).some(
    country => country === city,
  );

  let suitableForGenders = parseRouteParamToEnumArray(
    routeParamsString.suitableForGenders,
    GenderEnum,
  );

  suitableForGenders = filterArrayByAllowedValues(
    suitableForGenders,
    ListingsSearchAllowedSuitableForGenders,
  );

  let searchView = parseRouteParamToEnum(
    routeParamsString.searchView,
    SearchView,
  );

  searchView = valueExistsInArray(searchView, ListingsSearchAllowedSearchViews)
    ? searchView
    : SearchView.List;

  let roommateNumberMax = parseRouteParamToEnum(
    routeParamsString.roommateNumberMax,
    RoommateNumberID,
  );

  roommateNumberMax = valueExistsInArray(
    roommateNumberMax,
    ListingsSearchAllowedRoommateNumberMaxes,
  )
    ? roommateNumberMax
    : RoommateNumberID.Empty;

  let roommateGenders = parseRouteParamToEnumArray(
    routeParamsString.roommateGenders,
    GenderEnum,
  );

  roommateGenders = filterArrayByAllowedValues(
    roommateGenders,
    ListingsSearchAllowedRoommateGenders,
  );

  const searchCategories =
    mainSearchCategory === ListingsSearchCategory.Generic
      ? parseRouteParamToEnumArray(
          routeParamsString.searchCategories,
          ListingsSearchCategory,
        )
      : [mainSearchCategory];

  return {
    city: isCountrySearch ? '' : city,
    street: routeParamsString.street || '',
    searchCategories,
    pageNo: parseRouteParamToInt(routeParamsString.pageNo) || 1,
    radius:
      parseRouteParamToEnum(routeParamsString.radius, RadiusID) ||
      RadiusID.Km10,
    minSize:
      parseRouteParamToEnum(routeParamsString.minSize, SurfaceMinimumID) ||
      SurfaceMinimumID.Empty,
    maxRent:
      parseRouteParamToEnum(routeParamsString.maxRent, RentalPriceID) ||
      RentalPriceID.Empty,
    suitableForStatusIds: parseRouteParamToEnumArray(
      routeParamsString.suitableForStatusIds,
      Statuses,
    ),
    searchView,
    psaIdentifier: routeParamsString.psaIdentifier || '',
    sort:
      parseRouteParamToEnum(routeParamsString.sort, RoomAdvertSortOptionID) ||
      RoomAdvertSortOptionID.NewestFirst,
    furnishings: parseRouteParamToEnumArray(
      routeParamsString.furnishings,
      Furnishings,
    ),
    availabilityPeriods: parseRouteParamToEnumArray(
      routeParamsString.availabilityPeriods,
      AvailabilityPeriodEnum,
    ),
    availableFrom: routeParamsString.availableFrom || '',
    hasInternet:
      parseRouteParamToBoolean(routeParamsString.hasInternet) ?? false,
    isBathroomPrivate:
      parseRouteParamToBoolean(routeParamsString.isBathroomPrivate) ?? false,
    isKitchenPrivate:
      parseRouteParamToBoolean(routeParamsString.isKitchenPrivate) ?? false,
    isToiletPrivate:
      parseRouteParamToBoolean(routeParamsString.isToiletPrivate) ?? false,
    suitableForNumberOfPersons:
      parseRouteParamToEnum(
        routeParamsString.suitableForNumberOfPersons,
        SuitableForNumberID,
      ) || SuitableForNumberID.Empty,
    suitableForGenders,
    suitableForAge:
      keepAgeInRange(parseRouteParamToInt(routeParamsString.suitableForAge)) ||
      0,
    suitableForWorkStatuses: parseRouteParamToEnumArray(
      routeParamsString.suitableForWorkStatuses,
      Statuses,
    ),
    isSmokingInsideAllowed:
      parseRouteParamToBoolean(routeParamsString.isSmokingInsideAllowed) ??
      false,
    isPetsInsideAllowed:
      parseRouteParamToBoolean(routeParamsString.isPetsInsideAllowed) ?? false,
    roommateNumberMax,
    roommateGenders,
    ownerTypes: parseRouteParamToEnumArray(
      routeParamsString.ownerTypes,
      OwnerTypeID,
    ),
    nwlat: parseRouteParamToFloat(routeParamsString.nwlat) || MAP_INITIAL_NWLAT,
    nwlng: parseRouteParamToFloat(routeParamsString.nwlng) || MAP_INITIAL_NWLNG,
    selat: parseRouteParamToFloat(routeParamsString.selat) || MAP_INITIAL_SELAT,
    selng: parseRouteParamToFloat(routeParamsString.selng) || MAP_INITIAL_SELNG,
    mapZoom:
      parseRouteParamToFloat(routeParamsString.mapZoom) || MAP_INITIAL_ZOOM,
    mapMarkerLat: parseRouteParamToFloat(routeParamsString.mapMarkerLat) || 0,
    mapMarkerLng: parseRouteParamToFloat(routeParamsString.mapMarkerLng) || 0,
  };
};

export function mapListinsSearchCategoriesToListingTypes(
  searchCategories: ListingsSearchCategory[] | null | undefined,
): RoomTypeID[] {
  if (!searchCategories) {
    return [];
  }

  const listingTypes: RoomTypeID[] = [];

  for (const searchCategory of searchCategories) {
    const listingType = SEARCH_CATEGORY_TO_LISTING_TYPE[searchCategory];

    if (listingType) {
      listingTypes.push(listingType);
    }
  }

  return listingTypes;
}

export function mapListingTypesToListinsSearchCategories(
  listingTypes: RoomTypeID[] | null | undefined,
): ListingsSearchCategory[] {
  if (!listingTypes) {
    return [];
  }

  return listingTypes.map(
    listingType => LISTING_TYPE_TO_SEARCH_CATEGORY[listingType],
  );
}

export const ListingsSearchCity = defineRoute<
  ListingsSearchRouteParams,
  ListingsSearchRouteParamsString
>({
  pathname: '/for-rent/[searchCategoryAndCity]',
  nlPathname: '/huren/[searchCategoryAndCity]',
  options: { isLandlordRoute: false, isPublic: true },
  routeParamsStringifier,
  routeParamsParser,
});

export const ListingsSearchStreet = defineRoute<
  ListingsSearchRouteParams,
  ListingsSearchRouteParamsString
>({
  pathname: '/for-rent/[searchCategoryAndCity]/[street]',
  nlPathname: '/huren/[searchCategoryAndCity]/[street]',
  options: { isLandlordRoute: false, isPublic: true },
  routeParamsStringifier,
  routeParamsParser,
});
